import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Spinner } from 'reactstrap';
import { Unlock } from 'react-feather';
import ConfirmationModal from './ConfirmationModal';
import {
  getOrganizationMatches as getOrganizationMatchesAction,
  unlockOrganizationMatches as unlockOrganizationMatchesAction,
} from '../ReduxToolkit/Reducers/matches';

const EMPTY = { items: [], total: 0, locked_count: 0, locked_cost: 0, target_type: 'retailer' };
const credits = (n) => `${n.toLocaleString()} credit${n === 1 ? '' : 's'}`;

// The organization's matches as its Matches tab shows them. Unlocking here is free for the
// organization; the cost is what the customer would pay in the app.
const OrganizationMatches = ({ organizationId, organizationName, getOrganizationMatches, unlockOrganizationMatches }) => {
  const [data, setData] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [unlockingId, setUnlockingId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUnlockingAll, setIsUnlockingAll] = useState(false);

  const load = async () => {
    const response = await getOrganizationMatches({
      organization_id: organizationId,
      skip: (page - 1) * perPage,
      take: perPage,
    });
    if (response?.statusCode === 200) setData(response.data);
  };

  useEffect(() => {
    const loadPage = async () => {
      setIsLoading(true);
      await load();
      setIsLoading(false);
    };
    loadPage();
  }, [organizationId, page, perPage]);

  const handleUnlock = async (row) => {
    setUnlockingId(row.id);
    const response = await unlockOrganizationMatches({ organization_id: organizationId, target_ids: [row.id] });
    if (response?.statusCode === 200) await load();
    setUnlockingId(null);
  };

  const handleUnlockAll = async () => {
    setIsUnlockingAll(true);
    const response = await unlockOrganizationMatches({ organization_id: organizationId });
    if (response?.statusCode === 200) {
      const { unlocked } = response.data;
      toast.success(`${unlocked.toLocaleString()} match${unlocked === 1 ? '' : 'es'} unlocked. No credits were charged.`);
      await load();
    }
    setIsUnlockingAll(false);
    setIsConfirmOpen(false);
  };

  const columns = [
    {
      name: 'Name',
      minWidth: '220px',
      cell: (row) => (
        <Link target="_blank" to={`/profile/${row.id}/${data.target_type}`} title={row.website || undefined}>
          {row.name || `#${row.id}`}
        </Link>
      ),
    },
    { name: 'Location', selector: (row) => row.country || '--' },
    { name: 'Grade', width: '90px', selector: (row) => row.grade || '--' },
    { name: 'Priority', width: '100px', selector: (row) => row.priority || '--' },
    { name: 'Match Rate', width: '120px', selector: (row) => `${row.match_rate}%` },
    { name: 'Cost', width: '110px', selector: (row) => credits(row.credit_cost) },
    {
      name: 'Status',
      width: '140px',
      cell: (row) =>
        row.isUnlocked ? (
          <Badge color="success">Unlocked</Badge>
        ) : (
          <Button
            size="sm"
            color="primary"
            outline
            aria-label={`Unlock ${row.name}`}
            disabled={!!unlockingId || isUnlockingAll}
            onClick={() => handleUnlock(row)}
          >
            {unlockingId === row.id ? <Spinner size="sm" /> : 'Unlock'}
          </Button>
        ),
    },
  ];

  return (
    <Card className="shadow-sm mb-4">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
          <div>
            <h5 className="mb-1">Matches ({data.total.toLocaleString()})</h5>
            <small className="text-muted">
              {data.locked_count.toLocaleString()} locked. Unlocking here is free for the organization; Cost is what the customer would pay in the app.
            </small>
          </div>
          <Button
            color="primary"
            className="d-inline-flex align-items-center gap-2"
            disabled={!data.locked_count || !!unlockingId}
            onClick={() => setIsConfirmOpen(true)}
          >
            <Unlock size={16} />
            Unlock All ({data.locked_count.toLocaleString()})
          </Button>
        </div>
        <DataTable
          striped
          pagination
          paginationServer
          data={data.items}
          columns={columns}
          keyField="id"
          progressPending={isLoading}
          paginationTotalRows={data.total}
          paginationPerPage={perPage}
          paginationDefaultPage={page}
          onChangePage={setPage}
          onChangeRowsPerPage={(value) => {
            setPerPage(value);
            setPage(1);
          }}
          noDataComponent={<div className="p-4 text-muted">No matches for this organization</div>}
        />
      </CardBody>
      <ConfirmationModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onSubmit={handleUnlockAll}
        isLoading={isUnlockingAll}
        title="Unlock all matches?"
        message={`This unlocks ${data.locked_count.toLocaleString()} locked matches for ${organizationName || 'this organization'}. No credits are charged (${credits(data.locked_cost)} in the app). You can't undo this from here.`}
        confirmButtonText="Unlock All"
      />
    </Card>
  );
};

const mapDispatchToProps = {
  getOrganizationMatches: getOrganizationMatchesAction,
  unlockOrganizationMatches: unlockOrganizationMatchesAction,
};

export default connect(null, mapDispatchToProps)(OrganizationMatches);
